import { ROUTE_NAMES } from '@/constant/routes'

// AHA-46604 — the workspace-app → presenter "return to the workspace" contract.
//
// The workspace app (embed `aha-workspace`) opens editors with a full
// top-window navigation carrying `?from=workspace`. Consumers:
//   • presentation editor backV4Action (editor.module.js) — provenance-guarded
//     history.back() or a deterministic My Files push;
//   • EmbedAppContainer — the aha-survey editor's exit sentinel picks My Files
//     over a blind history step when this signal is on the route.
// The producer lives in workspace-app's frontend/src/host/navigate.ts:
// its FROM_WORKSPACE holds the full 'from=workspace' pair; this constant is
// only the VALUE — functionally consistent, deliberately not literal mirrors.
export const WORKSPACE_FROM_QUERY = 'workspace'

// Manifest id + canonical presenter address of the workspace app (My Files
// root — the EMBED_APP route the sidebar header links to). They live here,
// not in the sidebar module, so store code can target the workspace without
// reaching into components-v2; myFilesMenu re-exports both, so sidebar
// consumers are unchanged.
export const WORKSPACE_APP_ID = 'aha-workspace'
// path: '' (STRING), not [] — even though embedPathToString below argues the
// array is the "correct" shape for this repeatable param. The two coexist on
// purpose: this fallback is kept byte-identical to the pre-fix behaviour the
// sidebar header already produces (`/apps/addons/aha-workspace/`, trailing
// slash), and a test pins that. Both shapes match the same route, so nothing
// downstream cares; only the resolved trailing slash differs.
export const myFilesRoute = () => ({
  name: ROUTE_NAMES.EMBED_APP,
  params: { appId: WORKSPACE_APP_ID, path: '' },
})

// The EMBED_APP route declares `:path(.*)*`, a REPEATABLE param, and
// vue-router hands it over in TWO shapes: a STRING when the route was matched
// from a URL (page load, child-reported push), and an ARRAY of segments when a
// named-route push filled it (workspaceBackRoute below does exactly that — a
// joined string would be percent-encoded to 'folder%2F51').
//
// Every consumer of that param has to collapse both shapes, and this lived as
// two independent copies — one in myFilesMenu for the sidebar, none in
// EmbedAppContainer. The missing copy is what let an array reach `.replace()`
// and throw inside a computed, which a catch swallowed into a blank embed. The
// two copies had also already drifted on trailing-slash policy. One home,
// same reasoning that moved WORKSPACE_APP_ID/myFilesRoute here.
//
// Slash trimming stays with each caller: the sidebar trims both ends for
// active-state matching, the container trims only the leading slash because it
// also normalises child-reported locations. Only the shape collapse is shared.
export const embedPathToString = (value) => {
  const joined = Array.isArray(value) ? value.filter(Boolean).join('/') : value
  return typeof joined === 'string' ? joined : ''
}

// Query key holding WHERE in the workspace an editor was opened from, so the
// exit returns to that folder / kind subtab instead of the My Files root.
// `from=workspace` alone is a boolean — it says THAT you came from the
// workspace, never WHERE from, which is why the first cut always landed on
// the root. Producer: workspace-app's frontend/src/host/navigate.ts.
export const WORKSPACE_PATH_QUERY = 'fromPath'

// One workspace path segment. Deliberately an ALLOW-LIST, and deliberately
// narrow: the workspace app's own routes are only '', 'presentations',
// 'surveys' and 'folder/<id>', where the id is a server-issued serial INT
// (workspace-app's api/folders.ts types it `number`). Digits alone would
// cover every value in use today; the wider set is slack for ids/segments
// that may follow, not a claim that any of them exist yet.
//
// SECURITY — this value arrives as a query param on a presenter URL, i.e. it
// is fully attacker-controllable (copied links, crafted links). It ends up as
// the EMBED_APP `path` param, which EmbedAppContainer resolves with
// `new URL(path, app.baseUrl)`: 'https://evil.example' or '//evil.example'
// would resolve OFF the workspace origin and point the embedded iframe at an
// attacker's page. Excluding '.', ':' and '%' from the set is what blocks
// schemes, traversal and encoded re-entry — so a hostile value can never be
// partially cleaned into something that still resolves. Anything unrecognised
// falls back to the root rather than being escaped.
const WORKSPACE_PATH_SEGMENT = /^[A-Za-z0-9\-_]+$/

// Bounded so a crafted link can't hand the router an unreasonable location;
// the deepest real workspace path is 2 segments ('folder/<id>').
const MAX_WORKSPACE_PATH_SEGMENTS = 4

// Returns the path as ROUTE SEGMENTS, or [] when absent/unsafe.
//
// An array, not a string: EMBED_APP is declared 'addons/:appId/:path(.*)*' —
// a REPEATABLE param — and vue-router percent-encodes a multi-segment string
// into 'folder%2F51', which resolves to the wrong workspace location.
// (Verified against the real router, not assumed.)
const workspaceBackSegments = (raw) => {
  if (typeof raw !== 'string') return []
  const segments = raw.split('/').filter(Boolean)
  if (segments.length > MAX_WORKSPACE_PATH_SEGMENTS) return []
  // No empty-check needed: [].every() is vacuously true, so an empty path
  // falls through and returns [] anyway — the caller treats that as root.
  return segments.every((segment) => WORKSPACE_PATH_SEGMENT.test(segment))
    ? segments
    : []
}

// A raw `fromPath` that survives the allow-list. Same source of truth as
// workspaceBackRoute (no second regex to drift), just the boolean view.
const isSafeWorkspacePath = (raw) => workspaceBackSegments(raw).length > 0

// The route a workspace-launched editor exits to: the exact location it was
// opened from, falling back to the My Files root. The fallback returns
// myFilesRoute() itself so the no-path case stays byte-identical to the
// behaviour the sidebar and the first cut already produce.
export const workspaceBackRoute = (query) => {
  const path = workspaceBackSegments(query && query[WORKSPACE_PATH_QUERY])
  if (!path.length) return myFilesRoute()
  return {
    name: ROUTE_NAMES.EMBED_APP,
    params: { appId: WORKSPACE_APP_ID, path },
  }
}

// Several editor flows `replace()` the route query wholesale (preview
// open/close, present entry/exit, AI prompt consumption), which would wipe
// the signal mid-session. Merge helper that keeps exactly the workspace
// signal alive without resurrecting anything else those sites deliberately
// clear. Gated on the VALUE: `from` is an overloaded param in this app
// (audience, invitation, requestTemplate, …) and those must stay cleared.
export const withFromQuery = (currentQuery, nextQuery = {}) => {
  const from = currentQuery && currentQuery.from
  if (from !== WORKSPACE_FROM_QUERY) return nextQuery
  const merged = { ...nextQuery, from }
  // The return path rides WITH the flag through every wipe. Carrying the flag
  // alone would keep the exit working but silently degrade it to the root the
  // moment the user opened a preview.
  //
  // Validated HERE, not just at the consumers (defence-in-depth, Tam review):
  // every query wipe is a `$router.replace`, so an unchecked hostile
  // `fromPath=//evil.example` would be re-persisted into the address bar and
  // stay sticky for the whole session. Dropping it on the first wipe means a
  // crafted value never survives past one navigation; a genuine folder path is
  // unaffected. The consumers still re-validate — this doesn't replace that.
  const fromPath = currentQuery[WORKSPACE_PATH_QUERY]
  if (isSafeWorkspacePath(fromPath)) merged[WORKSPACE_PATH_QUERY] = fromPath
  return merged
}
